import './Networking.css'
import NetworkingOpt from "../../components/seo/NetworkingOpt";

export default function NetworkAccess() {
    return (
        <>
            <NetworkingOpt
                title="Network Access"
                description="Learn network access concepts including switching, collision domains, broadcast domains, VLANs, trunk ports, and 802.1Q tags."
                keywords="network access, switching, vlan, trunk port, 802.1q, collision domain, broadcast domain"
            />

            <article className='content'>
                <h1 style={{display:'flex', justifyContent:'center'}}>Network Access</h1>

  
                <h2>1. Switching Concepts</h2>
                
                <h3>Switch Function</h3>
                <p>The primary job of a switch is to forward frames to the correct destination MAC address.</p>
                <pre>Switches use logic based on source and destination MAC addresses in the Ethernet frame.</pre>

                <h3>Network Domains </h3>
                <table><tr><td>Term</td><td>Description</td></tr><tr><td>Collision Domain</td><td>Area where data packets can collide. Each switch port creates a separate collision domain.</td></tr><tr><td>Broadcast Domain</td><td>Group of devices that receive broadcast frames (destination MAC FF:FF:FF:FF:FF:FF).</td></tr><tr><td>Unicast Flooding</td><td>Occurs when a switch doesn’t know the destination MAC address and floods the frame to all ports except the source.</td></tr></table>
                
                <h3>Hub vs Switch</h3>
                <table><tr><td>Device</td><td>Function</td><td>Collision Domain</td><td>Broadcast Domain</td></tr><tr><td>Hub</td><td>Repeats signals to all ports</td><td>One (shared)</td><td>One</td></tr><tr><td>Switch</td><td>Forwards frames intelligently using MAC table</td><td>One per port</td><td>One per VLAN</td></tr></table>
                
                <h3>Forwarding Types</h3>
                <table><tr><td>Type</td><td>Description</td></tr><tr><td>Single Hop Forwarding</td><td>Frame reaches destination within one switch.</td></tr><tr><td>Multiple Hops</td><td>Frame passes through multiple switches before reaching destination.</td></tr></table>
              

           
                <h2>2. VLAN (Virtual Local Area Network) </h2>
            
                <h3>VLAN Configuration</h3>
           
                <pre>
                    <code>  SW1# show vlan brief</code><br />
                
                    <code>Switch(config)# vlan 10</code><br />
                    <code>Switch(config-vlan)# name articles</code><br />
                    <code>Switch(config-vlan)# exit</code><br />
                
                <br/>
                
                    <code>Switch(config)# interface FastEthernet0/1</code><br />
                    <code>Switch(config-if)# switchport mode access</code><br />
                    <code>Switch(config-if)# switchport access vlan 10</code><br />
                    <code>Switch(config-if)# exit</code><br />
                </pre>
                
                <h3>Trunk Configuration </h3>
                
                <pre>
                    <code>SW1(config)# interface g0/0</code><br />
                    <code>SW1(config-if)# switchport mode trunk</code><br />
                    <code>SW1(config-if)# switchport trunk encapsulation dot1q</code><br />
                    <code>SW1(config-if)# switchport trunk allowed vlan 10,20,30</code><br />
                    <code>SW1(config-if)# switchport trunk native vlan 1001</code><br />
                    <code>SW1(config-if)# do show interfaces trunk</code>
                </pre>
                <br/>
                <p>LAN: Group of network devices within the same broadcast domain.</p>
                <p>VLAN: Logical segmentation of a LAN into separate broadcast domains.</p>
                <p>Switches do not perform inter‑VLAN routing — traffic must go through a router or Layer 3 switch.</p>
                <p>VLANs are identified by numbers 1–4094, as defined by the IEEE 802.1Q standard.</p>
                <p>VLAN 1 is the default VLAN (used for management and control traffic unless changed).</p>
                <p>VLANs 1002–1005 are reserved for legacy Token Ring and FDDI.</p>
         

                <h2>3. Switch Port Types </h2>
        

                <h3>Access Port</h3>
                <p>Belongs to a single VLAN.</p>
                <p>Connects to end hosts (PCs, printers, IP phones).</p>

                <h3>Trunk Port</h3>
                <p>Carries multiple VLANs over a single interface.</p>
                <p>Frames are tagged with VLAN information.</p>
                <p>Trunking Protocols:</p>
                <p>ISL (Inter‑Switch Link) – Cisco proprietary (legacy).</p>
                <p>IEEE 802.1Q – Industry standard.</p>
                

                <h3>802.1Q Tag </h3>
                <p>Length: 4 bytes (32 bits).</p>
                <p>Inserted between Source MAC and Type/Length fields.</p>
                <p>Fields:</p>
                <p>TPID (Tag Protocol Identifier): Always 0x8100 (indicates 802.1Q tag).</p>
                <p>TCI (Tag Control Information): Contains VLAN ID (VID).</p>
                <p>VID: 12 bits → supports 4096 VLANs (0–4095).</p>
                <p>VLANs 0 and 4095 are reserved.</p>
                <p>Native VLAN: VLAN 1 by default; frames are not tagged.</p>


                <h2>4. Router on a Stick (ROAS) </h2>


                <h3>Configuration:</h3>

                <pre>
                    <code>R1(config)# interface g0/0</code><br />
                    <code>R1(config-if)# no shutdown</code><br />
                    <code>R1(config)# interface g0/0.10</code><br />
                    <code>R1(config-subif)# encapsulation dot1q 10</code><br />
                    <code>R1(config-subif)# ip address 192.168.1.62 255.255.255.192</code><br />
                    <code>R1(config)# interface g0/0.20</code><br />
                    <code>R1(config-subif)# encapsulation dot1q 20</code><br />
                    <code>R1(config-subif)# ip address 192.168.1.126 255.255.255.192</code>
                </pre>
                <p>Used for inter‑VLAN routing via router subinterfaces. </p>


                <h3>Inter-VLAN Routing via SVI</h3>

                <pre>Switch Virtual Interfaces, virtual interfaces in multilayer switch.</pre>
                <p>Configure PCs to use SVI as their gateway address.</p>



                <h2>5. Dynamic Trunking Protocol (DTP)</h2>

                <table><tr><td>Mode</td><td>Behavior</td></tr><tr><td>Dynamic Auto</td><td>Passive; forms trunk only if the other side is actively trying.</td></tr><tr><td>Dynamic Desirable</td><td>Actively tries to form a trunk. Works with trunk, desirable, or auto modes.</td></tr></table>

                <p>DTP does not form trunks with routers or PCs.</p>
                <p>Default encapsulation negotiation: ISL preferred over 802.1Q.</p>



                <h2>6. VLAN Trunking Protocol</h2>
                <table><tr><td>Mode</td><td>Description</td></tr><tr><td>Server</td><td>Can add/modify/delete VLANs; stores VLANs in NVRAM; advertises updates.</td></tr><tr><td>Client</td><td>Cannot modify VLANs; synchronizes VLAN database from server.</td></tr><tr><td>Transparent</td><td>Does not participate in the VTP domain; forwards advertisements only.</td></tr></table>

                <p>Allows centralized VLAN management across switches. </p>
                <p>Versions: VTP 1, 2, and 3.</p>
                <p>Revision number increases with VLAN changes; switches sync to the highest revision.</p>



                <h2>7. Spanning Tree Protocol (STP)</h2>


                <h3>Configuration:</h3>

                <pre>SW1(config)# spanning-tree portfast bpduguard default</pre>


                <h3>STP Key Concepts </h3>

                <table><tr><td>Term</td><td>Description</td></tr><tr><td>Root Bridge</td><td>Switch with lowest Bridge ID (priority + MAC). All ports are forwarding.</td></tr><tr><td>Bridge Priority</td><td>Default = 32768; increments in steps of 4096. VLAN 1 default = 32769.</td></tr><tr><td>Root Port</td><td>Port with lowest path cost to root bridge.</td></tr><tr><td>Designated Port</td><td>Forwarding port on each segment.</td></tr><tr><td>Blocking Port</td><td>Non‑designated port; backup path.</td></tr></table>

                <p>Prevents Layer 2 loops and broadcast storms by blocking redundant paths.</p>
                <p>Standard: IEEE 802.1D (Classic STP).</p>
                <p>Mechanism: Places redundant ports in blocking state.</p>
                <p>BPDUs (Bridge Protocol Data Units): Used for STP communication.</p>


                <h3>STP Port States </h3>

                <table><tr><td>State</td><td>Duration</td><td>Function</td></tr><tr><td>Blocking</td><td>Stable</td><td>Receives BPDUs only.</td></tr><tr><td>Listening</td><td>~15s</td><td>Receives/sends BPDUs; no data traffic.</td></tr><tr><td>Learning</td><td>~15s</td><td>Learn MAC addresses; no data traffic.</td></tr><tr><td>Forwarding</td><td>Active</td><td>Normal operation; sends/receives frames.</td></tr></table>


                <h3>STP Protection Features </h3>

                <table><tr><td>Feature</td><td>Description</td></tr><tr><td>PortFast</td><td>Immediately transitions port to forwarding; use only on end‑host ports.</td></tr><tr><td>BPDU Guard</td><td>Shuts down port if BPDU receives from another switch.</td></tr><tr><td>Root Guard</td><td>Prevents a new switch from becoming a root bridge.</td></tr><tr><td>Loop Guard</td><td>Prevents forwarding if BPDUs stop being received.</td></tr></table>



                <h2>8. Rapid Spanning Tree Protocol (RSTP)</h2>

                <p>Standard: IEEE 802.1W.</p>
                <p>Faster convergence using handshake mechanism (not timer‑based).</p>
                <p>Detects neighbor loss in 3 hello intervals (vs 10 in STP).</p>
                <p>Port States: Discarding, Learning, Forwarding.</p>
                <p>Port Roles: Root, Designated, Alternate, Backup.</p>
                <p>Link Types: Point‑to‑Point, Shared, Edge.</p>



                <h2>9. EtherChannel</h2>
                <p>Combines multiple physical links into one logical link for redundancy and load balancing.</p>
                <p>Also called Port Channel or Link Aggregation Group (LAG).</p>
                <p>Traffic load‑balanced per flow (same flow uses same physical link).</p>
                <p>Protocols:</p>
                <p>PAgP (Cisco proprietary)</p>
                <p>LACP (IEEE 802.3ad)</p>
                <p>Static (manual configuration)</p>


                <h3>Port Aggregation Protocol (PAgP)</h3>

                <p><em>Configuration:</em></p>
                <pre>SW(config-if-range)# channel-group 1 mode desirable</pre>

                <table><tr><td>Combination</td><td>Result</td></tr><tr><td>Auto + Auto</td><td>❌ No EtherChannel</td></tr><tr><td>Desirable + Auto</td><td>✅ EtherChannel</td></tr><tr><td>Desirable + Desirable</td><td>✅ EtherChannel</td></tr></table>


                <h3>Link Aggregation Control Protocol (LACP) </h3>

                <p><em>Configuration on Switch 1:</em></p>

                <pre>
                    <code>Switch2&gt; enable</code><br />
                    <code>Switch2# configure terminal</code><br />
                    <code>Switch2(config)# interface range fa0/1 - 2</code><br />
                    <code>Switch2(config-if-range)# channel-group 1 mode active</code><br />
                    <code>Switch2(config-if-range)# exit</code><br />
                    <code>Switch2(config)# interface port-channel 1</code><br />
                    <code>Switch2(config-if)# switchport mode trunk</code><br />
                    <code>Switch2(config-if)# switchport trunk allowed vlan 10,20,30</code><br />
                    <code>Switch2(config-if)# exit</code><br />
                </pre>


                <p><em>Configuration on Switch 2:</em></p>

                <pre>
                    <code>Switch3&gt; enable</code><br />
                    <code>Switch3# configure terminal</code><br />
                    <code>Switch3(config)# interface range fa0/1 - 2</code><br />
                    <code>Switch3(config-if-range)# channel-group 1 mode passive</code><br />
                    <code>Switch3(config-if-range)# exit</code><br />
                    <code>Switch3(config)# interface port-channel 1</code><br />
                    <code>Switch3(config-if)# switchport mode trunk</code><br />
                    <code>Switch3(config-if)# switchport trunk allowed vlan 10,20,30</code><br />
                    <code>Switch3(config-if)# exit</code><br />
                </pre>

                <p><em>Verification Commands:</em></p>

                <pre>
                    <code>Switch# show etherchannel summary</code><br />
                    <code>Switch# show interfaces port-channel 1</code><br />
                    <code>Switch# show spanning-tree</code><br />
                </pre>

                <table><tr><td>Combination</td><td>Result</td></tr><tr><td>Passive + Passive</td><td>❌ No EtherChannel</td></tr><tr><td>Active + Active</td><td>✅ EtherChannel</td></tr><tr><td>Active + Passive</td><td>✅ EtherChannel</td></tr></table>

                <p>Up to 8 active and 8 standby interfaces per EtherChannel. </p>



                <h2>10. Wireless Standards </h2>


                <h3>IEEE 802.11 Family</h3>

                <table><tr><td>Standard</td><td>Frequency Band</td><td>Max Speed</td><td>Key Features</td></tr><tr><td>802.11a</td><td>5 GHz</td><td>54 Mbps</td><td>Less interference, shorter range</td></tr><tr><td>802.11b</td><td>2.4 GHz</td><td>11 Mbps</td><td>Longer range, more interference</td></tr><tr><td>802.11g</td><td>2.4 GHz</td><td>54 Mbps</td><td>Backward compatible with 802.11b</td></tr><tr><td>802.11n (Wi-Fi 4)</td><td>2.4 &amp; 5 GHz</td><td>Up to 600 Mbps</td><td>MIMO (Multiple Input Multiple Output)</td></tr><tr><td>802.11ac (Wi-Fi 5)</td><td>5 GHz</td><td>Up to several Gbps</td><td>Wider channels, more MIMO streams</td></tr><tr><td>802.11ax (Wi-Fi 6)</td><td>2.4 &amp; 5 GHz</td><td>Up to 9.6 Gbps</td><td>OFDMA, Target Wake Time (TWT), efficiency in dense environments</td></tr></table>
                <p>Frequency Bands</p>
                <table><tr><td>Band</td><td>Pros</td><td>Cons</td></tr><tr><td>2.4 GHz</td><td>Greater range, better wall penetration</td><td>More interference, fewer channels</td></tr><tr><td>5 GHz</td><td>Higher speed, less interference</td><td>Shorter range, weaker penetration</td></tr></table>


                <h3>Wireless Components</h3>


                <h4>Access Point (AP)</h4>
                <p>Device that connects wireless clients to a wired network.</p>
                <table><tr><td>Type</td><td>Description</td></tr><tr><td>Autonomous AP</td><td>Standalone, individually configured</td></tr><tr><td>Lightweight AP (LAP)</td><td>Managed by a Wireless LAN Controller (WLC)</td></tr></table>
                <h4>Wireless LAN Controller (WLC)</h4>
                <p>Centralized management for multiple APs — handles configuration, monitoring, and security.</p>
                <p>Protocol: CAPWAP (Control And Provisioning of Wireless Access Points) enables AP-WLC communication.</p>
                
                <h4>Wireless Clients</h4>
                <p>Devices such as laptops, smartphones, and tablets that connect to the wireless network.</p>
                
                <h4>Antennas</h4>
                <table><tr><td>Type</td><td>Coverage</td><td>Use Case</td></tr><tr><td>Omnidirectional</td><td>360° coverage</td><td>General indoor use</td></tr><tr><td>Directional</td><td>Focused beam</td><td>Point-to-point links or outdoor coverage</td></tr></table>
                
                <h4>RF Fundamentals</h4>

                <table><tr><td>Concept</td><td>Description</td></tr><tr><td>SSID (Service Set Identifier)</td><td>Network name visible to clients</td></tr><tr><td>BSS (Basic Service Set)</td><td>Single AP and its clients</td></tr><tr><td>ESS (Extended Service Set)</td><td>Multiple APs providing seamless coverage</td></tr><tr><td>Channels</td><td>2.4 GHz non-overlapping: 1, 6, 11</td></tr><tr><td>RSSI (Received Signal Strength Indicator)</td><td>Measures signal strength for connectivity quality</td></tr></table>


                <h3>Wireless Security</h3>


                <h4>Authentication Methods</h4>
                <table><tr><td>Method</td><td>Description</td><td>Security Level</td></tr><tr><td>Open System</td><td>No authentication</td><td>❌ Insecure</td></tr><tr><td>Shared Key (WEP)</td><td>Static key</td><td>❌ Obsolete</td></tr><tr><td>WPA / WPA2 / WPA3</td><td>Modern encryption standards</td><td>✅ Secure</td></tr></table>
                <h4>Security Modes</h4>
                <table><tr><td>Mode</td><td>Description</td><td>Use Case</td></tr><tr><td>Personal (PSK)</td><td>Pre-shared key (passphrase)</td><td>Home / small office</td></tr><tr><td>Enterprise (802.1X / EAP)</td><td>Uses RADIUS server for authentication</td><td>Corporate networks</td></tr></table>
                <h4>Key Protocols</h4>
                <p>802.1X: Port-based access control standard.</p>
                <p>EAP (Extensible Authentication Protocol): Framework for authentication (EAP-TLS, PEAP, EAP-FAST).</p>
                <p>RADIUS: Used by WLC/AP to communicate with authentication servers (e.g., Cisco ISE, Microsoft NPS).</p>
                <p>Encryption: WPA2/WPA3 use AES for strong encryption.</p>
                <p>Guest Networks: Isolated SSIDs with captive portals.</p>
                <p>MAC Filtering: Weak security; easily bypassed.</p>
                <p>Basic Wireless Configuration &amp; Management</p>


                <h4>Common Configuration Tasks</h4>
                <p>Create SSID: Define network name.</p>
                <p>Apply Security: Configure WPA2-PSK or WPA2-Enterprise.</p>
                <p>Select Channel: Choose non-overlapping channels.</p>
                <p>Manage via WLC GUI: Configure APs, SSIDs, and security centrally.</p>
                <p>Set AP Mode:</p>
                <p>Autonomous Mode: Standalone operation.</p>
                <p>Lightweight Mode: Controlled by WLC.</p>


                <h4>Wireless Troubleshooting</h4>
                <p>Common Issues</p>
                <p>No connectivity</p>
                <p>Intermittent drops</p>
                <p>Slow speeds</p>
                <p>Weak signal strength</p>
                <p>Incorrect security configuration</p>
                <p>Co-channel interference</p>


                <h4>Tools &amp; Techniques</h4>
                <table><tr><td>Tool</td><td>Purpose</td></tr><tr><td>ipconfig /all</td><td>Verify IP and adapter configuration</td></tr><tr><td>Wi-Fi Analyzer Apps</td><td>Check signal strength and channel overlap</td></tr><tr><td>WLC/AP Logs</td><td>Identify authentication or connectivity errors</td></tr><tr><td>Ping / Traceroute</td><td>Test connectivity and latency</td></tr></table>

            </article>


        </>
    )

};
